'use client';

import cx from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PropertyType,
  useProperty,
} from '@/forApp/states/propertyState/propertyState';
import Button from '@/shared/Buttons/Button';
import Checkbox from '@/shared/Checkboxs/Checkbox';
import ControlledInput from '@/shared/Input/ControlledInput';
import InputRange from '@/shared/Inputs/InputRage/InputRange';
import RadioGroup from '@/shared/RadioGroup/RadioGroup';
import SimpleSelect, { type ItemSelectT } from '@/shared/Selects/Select';
import ToggleSwitcher from '@/shared/ToggleSwitcher/ToggleSwitcher';
import type { DefaultFilterT } from '@/widgets/MainBlock/MainSearch/types';
import AdTypeTabs from './AdTypeTabs';
import styles from './styles.module.css';
import { getRouteHref, ROUTES } from '@/forApp/utils/routes';

// Extracted helper to reset all filters except real estate type and go to the first page
const resetAllFilters = ({
  searchParams,
  pathname,
  router,
  clearPending,
}: {
  searchParams: ReturnType<typeof useSearchParams>;
  pathname: string;
  router: ReturnType<typeof useRouter>;
  clearPending: () => void;
}) => {
  try {
    const params = new URLSearchParams(searchParams?.toString() || '');
    // Remove all filter-related query params (handles both raw and encoded keys)
    Array.from(params.keys()).forEach((key) => {
      const isFilterKey =
        key.startsWith('filters') ||
        key.startsWith('filters[') ||
        key.startsWith('filters%5B');
      if (isFilterKey) {
        params.delete(key);
      }
    });
    // Remove legacy key if present
    params.delete('extraType');
    // Reset pagination
    params.set('page', '1');
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    // Clear staged pending changes and scroll to top
    clearPending();
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  } catch (e) {
    console.error('Failed to reset filters', e);
  }
};

// Helper for staging URL params and applying them only on button click
const useUrlFilters = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // pending changes that are not applied to URL yet
  const [pending, setPending] = useState<
    Record<string, string | string[] | null | undefined>
  >({});

  const eqArrays = (a: string[] = [], b: string[] = []) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  // read helpers consider pending first
  const getParam = (key: string) => {
    const v = Object.hasOwn(pending, key) ? pending[key] : undefined;
    if (Array.isArray(v)) return v[0] || '';
    if (typeof v === 'string') return v;
    if (v === null) return '';
    return searchParams?.get(key) || '';
  };
  const getAll = (key: string) => {
    const v = Object.hasOwn(pending, key) ? pending[key] : undefined;
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') return v ? [v] : [];
    if (v === null) return [];
    return searchParams ? searchParams.getAll(key) : [];
  };

  // stage a param change; do not touch URL yet
  const setParam = (
    key: string,
    value: string | string[] | null | undefined,
  ) => {
    let next: string | string[] | null | undefined = value;
    if (Array.isArray(next)) next = next.filter(Boolean) as string[];

    // Compare to current URL value
    const currentAll = searchParams ? searchParams.getAll(key) : [];
    const isSame = Array.isArray(next)
      ? eqArrays(next as string[], currentAll)
      : typeof next === 'string'
        ? currentAll[0] === next
        : next === null || next === undefined
          ? currentAll.length === 0
          : false;
    setPending((prev) => {
      const copy = { ...prev };
      console.log(copy, 'copy');
      console.log(copy[key], 'copy[key]');
      if (isSame) {
        delete copy[key];
      } else {
        copy[key] = next;
      }
      return copy;
    });
  };

  const hasChanges = useMemo(() => Object.keys(pending).length > 0, [pending]);

  const applyPending = () => {
    if (!hasChanges) return;
    const params = new URLSearchParams(searchParams?.toString() || '');
    let touchedNonPage = false;
    Object.entries(pending).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        params.delete(key);
        (value as string[]).forEach((v) => params.append(key, v));
      } else if (value === null || value === undefined || value === '') {
        params.delete(key);
      } else {
        params.set(key, value as string);
      }
      if (key !== 'page') touchedNonPage = true;
    });
    if (touchedNonPage) params.set('page', '1');
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    setPending({});
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  };

  const clearPending = () => setPending({});
  return { setParam, getParam, getAll, hasChanges, applyPending, clearPending };
};

const findParentOfChild = (
  childSlug: string,
  types: PropertyType[],
): string | null => {
  for (const t of types) {
    if (t.entityTypes?.some((st) => st.slug === childSlug)) return t.slug;
  }
  return null;
};

type CatalogFiltersProps = {
  type: string;
  filters: DefaultFilterT;
  setLoad?: (load: boolean) => void;
  isMaps?: boolean;
  isMobile?: boolean;
  setIsOpenFilter?: (isOpen: boolean) => void;
};

const CatalogFilters: React.FC<CatalogFiltersProps> = ({
  type,
  filters,
  setLoad,
  isMaps,
  isMobile,
  setIsOpenFilter,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isFixed, setIsFixed] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { types, fetchTypes } = useProperty();
  const { setParam, getParam, getAll, hasChanges, applyPending, clearPending } =
    useUrlFilters();
  const allowFilters = filters.allowFilters || [];
  const moreFilter = filters.moreFilter || [];
  const currentCity = searchParams.get('filters[city]');

  useEffect(() => {
    if (!types.length) {
      fetchTypes().then();
    }
  });

  const handleChangeType = (type: string) => {
    const params = new URLSearchParams(searchParams?.toString() || '');
    // remove legacy extraType from query if present
    params.delete('extraType');
    // remove all existing filters from query on type change
    Array.from(params.keys()).forEach((key) => {
      // covers both raw and encoded keys like filters[...]
      const isFilterKey =
        key.startsWith('filters') ||
        key.startsWith('filters[') ||
        key.startsWith('filters%5B');
      const isAdTypeKey =
        key === 'filters[ad_type]' ||
        key === 'filters%5Bad_type%5D' ||
        key === 'filters[ad_type][]';
      if (isFilterKey && !isAdTypeKey) {
        params.delete(key);
      }
    });
    // reset pagination
    params.set('page', '1');

    const newPath = getRouteHref(isMaps ? ROUTES.MAP : ROUTES.CATALOG, {
      type,
    });
    const qs = params.toString();

    router.push(`${newPath}?${qs}`, { scroll: false });
  };

  const handleChangeCity = (city: string) => {
    const params = new URLSearchParams(searchParams?.toString() || '');
    params.set('filters[city]', city);
    const qs = params.toString();
    router.push(`${pathname}?${qs}`, { scroll: false });
  };

  // Decide which top-level group is active to expand
  const activeTop = useMemo(() => {
    if (!types?.length) return '';
    // If current [type] is a child, expand its parent group
    const parent = findParentOfChild(type, types);
    if (parent) return parent;
    // Otherwise [type] is a top-level slug
    return type;
  }, [types, type]);

  // Local override for expanding a top-level group without navigation (e.g., for "Коммерческая")
  const [expandedTop, setExpandedTop] = useState<string>('');

  // Track whether the button bar should be fixed at the viewport bottom
  useEffect(() => {
    const handler = () => {
      const node = contentRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const contentFits = rect.height <= viewportH;
      if (contentFits) {
        setIsFixed(true);
      } else {
        // While we haven't reached the end of content, keep it fixed; once reached, make it static
        setIsFixed(rect.bottom > viewportH);
      }
    };
    handler();
    window.addEventListener('scroll', handler, { passive: true } as any);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler as any);
      window.removeEventListener('resize', handler);
    };
  }, []);

  const hasFilters = Array.isArray(allowFilters?.filters);
  const hasCities =
    Array.isArray(allowFilters?.cities) && allowFilters.cities.length > 0;

  const selectedCityOption: ItemSelectT = (hasCities &&
    allowFilters.cities.find((opt) => opt.title === currentCity)) || {
    value: '',
    title: 'Все города',
  };

  return (
    <section
      className={cx(
        isMaps
          ? isMobile
            ? styles.catalogFiltersMobile
            : styles.catalogFiltersMaps
          : isMobile
            ? styles.catalogFiltersMobile
            : styles.catalogFilters,
      )}
    >
      {!isMobile && (
        <div className={styles.headFilters}>
          <h4 className={styles.catalogFiltersTitle}>Фильтры</h4>
          <Button
            text={'Сбросить всё'}
            onClick={() => {
              resetAllFilters({ searchParams, pathname, router, clearPending });
            }}
            variant={'link'}
          />
        </div>
      )}
      <div ref={contentRef}>
        {!!types?.length && (
          <div className={styles.mb16}>
            <div className={styles.categoriesList}>
              {types.map((t) => {
                const effectiveActiveTop = expandedTop || activeTop;
                const topActive = effectiveActiveTop === t.slug;
                const isSelectedTop = effectiveActiveTop === t.slug;
                return (
                  <div key={t.slug}>
                    <button
                      type="button"
                      onClick={() => {
                        // Always toggle expansion on top-level; do not navigate on top-level clicks
                        setExpandedTop((prev) =>
                          prev === t.slug ? '' : t.slug,
                        );
                      }}
                      className={`${styles.categoryBtn} ${isSelectedTop ? styles.categoryBtnSelected : ''}`}
                    >
                      {t.name}
                    </button>
                    {topActive &&
                      Array.isArray(t.entityTypes) &&
                      t.entityTypes.length > 0 && (
                        <div className={styles.subCategoriesList}>
                          {t.entityTypes.map((st) => {
                            const childSelected = type === st.slug;
                            return (
                              <button
                                key={st.slug}
                                type="button"
                                onClick={() => handleChangeType(st.slug)}
                                className={`${styles.childCategoryBtn} ${childSelected ? styles.childCategoryBtnSelected : ''}`}
                              >
                                {st.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <AdTypeTabs />

        {/* allowFilters based block (city + predefined ranges + rooms) */}
        {hasFilters && (
          <div className={styles.mb16}>
            {hasCities && (
              <div className={styles.mb12}>
                <SimpleSelect
                  options={[
                    { value: '', title: 'Все города' },
                    ...allowFilters.cities,
                  ]}
                  selected={selectedCityOption}
                  onChange={(option) => handleChangeCity(option?.title || '')}
                  placeholder={'Город'}
                  instanceId="catalog-city-select"
                />
              </div>
            )}

            <div className={styles.stackedInputs}>
              {allowFilters.filters.map((f: any, idx: number) => {
                if (
                  f?.type === 'InputRange' &&
                  Array.isArray(f.filters) &&
                  f.filters.length >= 2
                ) {
                  const fromKey = f.filters[0].filter;
                  const toKey = f.filters[1].filter;
                  return (
                    <InputRange
                      key={`af-range-${idx}`}
                      filterName={f.title || f.type}
                      onChangeFrom={(k, v) => setParam('filters' + k, v)}
                      onChangeTo={(k, v) => setParam('filters' + k, v)}
                      fromPlaceholder={f.filters[0].name || 'От'}
                      toPlaceholder={f.filters[1].name || 'До'}
                      fromKey={fromKey}
                      toKey={toKey}
                      initialValues={[
                        searchParams?.get('filters' + fromKey) || '',
                        searchParams?.get('filters' + toKey) || '',
                      ]}
                      size={
                        isMaps
                          ? isMobile && window
                            ? window.innerWidth / 2 - 20
                            : 180
                          : isMobile && window
                            ? window.innerWidth / 2 - 16
                            : 145
                      }
                    />
                  );
                }
                return null;
              })}
            </div>

            {allowFilters.filters.some(
              (f: any) => f.title === 'Комнатность',
            ) && (
              <div className={styles.mt16}>
                <div className={styles.headFiltersSimple}>
                  <span className={styles.sectionLabel}>Комнатность</span>
                  {/*    <Button text={'Сбросить'} onClick={()=>{}} variant={'link'}/>
                   */}
                </div>
                <div>
                  {allowFilters.filters
                    .find((f: any) => f.title === 'Комнатность')
                    ?.filters?.map((data: any, i: number) => {
                      const isRoomsArray = data.filter === '[kol-vo-komnat]';
                      const key = isRoomsArray
                        ? 'filters[kol-vo-komnat][]'
                        : `filters${data.filter}`;
                      const value = String(data.value?.[0] ?? '');
                      const current = isRoomsArray
                        ? getAll(key) || []
                        : [getParam(key) || ''];
                      const checked = isRoomsArray
                        ? current.includes(value)
                        : current[0] === value;
                      return (
                        <label
                          key={`rooms-${i}`}
                          className={styles.checkboxRow}
                        >
                          <Checkbox
                            isChecked={!!checked}
                            onChange={() => {
                              if (isRoomsArray) {
                                const next = new Set(current);
                                if (checked) {
                                  next.delete(value);
                                } else {
                                  next.add(value);
                                }
                                setParam(key, Array.from(next) as string[]);
                              } else {
                                setParam(key, checked ? null : value);
                              }
                            }}
                          />
                          <span>{data.name}</span>
                        </label>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {moreFilter.map((block) => (
          <React.Fragment key={block.id}>
            {block.properties.map((data) => (
              <div
                key={data.id}
                className={`${styles.mb16} ${data.property_type === 'boolean' ? styles.wrapBool : ''}`}
              >
                <span className={styles.sectionLabel}>{data.title}</span>

                {data.filter_type === 'common' ? (
                  <div>
                    {data.component === 'Checkbox' && data.data && (
                      <div>
                        {data.data.map((option: string, idx: number) => {
                          const key = `filters[${data.slug}][]`;
                          const values = getAll(key);
                          const present = values.includes(option);
                          return (
                            <label key={idx} className={styles.checkboxRow}>
                              <Checkbox
                                isChecked={present}
                                onChange={() => {
                                  const next = new Set(values);
                                  if (present) {
                                    next.delete(option);
                                  } else {
                                    next.add(option);
                                  }
                                  setParam(key, Array.from(next) as string[]);
                                }}
                              />
                              <span>{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {data.component === 'RadioButton' && data.data && (
                      <RadioGroup
                        name={data.slug}
                        options={data.data}
                        value={getParam(`filters[${data.slug}][]`)}
                        onChange={(value) =>
                          setParam(`filters[${data.slug}][]`, value)
                        }
                        className={styles.radioFilter}
                      />
                    )}

                    {data.component === 'RadioButtonPills' && data.data && (
                      <RadioGroup
                        name={data.slug}
                        options={data.data}
                        value={getParam(`filters[${data.slug}][]`)}
                        onChange={(value) =>
                          setParam(`filters[${data.slug}][]`, value)
                        }
                        className={styles.radioPills}
                      />
                    )}

                    {data.property_type === 'string' && (
                      <ControlledInput
                        type="text"
                        className={''}
                        placeholder={data.title}
                        value={getParam(`filters[${data.slug}]`) || ''}
                        onChange={(value) =>
                          setParam(`filters[${data.slug}]`, value)
                        }
                      />
                    )}

                    {data.property_type === 'int' && (
                      <InputRange
                        filterName={data.title}
                        onChangeFrom={(k, v) => setParam(k, v)}
                        onChangeTo={(k, v) => setParam(k, v)}
                        fromPlaceholder="От"
                        toPlaceholder="До"
                        fromKey={`filters[${data.slug}][from]`}
                        toKey={`filters[${data.slug}][to]`}
                        initialValues={[
                          searchParams?.get(`filters[${data.slug}][from]`) ||
                            '',
                          searchParams?.get(`filters[${data.slug}][to]`) || '',
                        ]}
                        size={
                          isMaps
                            ? isMobile && window
                              ? window.innerWidth / 2 - 20
                              : 180
                            : isMobile && window
                              ? window.innerWidth / 2 - 20
                              : 145
                        }
                      />
                    )}

                    {data.property_type === 'boolean' &&
                      data.component !== 'RadioButton' &&
                      data.component !== 'RadioButtonPills' && (
                        <ToggleSwitcher
                          name={`filters[${data.slug}]`}
                          value={getParam(`filters[${data.slug}]`) || '0'}
                          onChange={(value) =>
                            setParam(
                              `filters[${data.slug}]`,
                              value === '1' ||
                                value === 'true' ||
                                value === true
                                ? '1'
                                : null,
                            )
                          }
                          size={'small'}
                        />
                      )}
                  </div>
                ) : (
                  <div>
                    {data.component === 'Checkbox' && data.data && (
                      <div>
                        {data.data.map((option: string, idx: number) => {
                          const key = `filters[${data.slug}][]`;
                          const values = getAll(key);
                          const present = values.includes(option);
                          return (
                            <label key={idx} className={styles.checkboxRow}>
                              <Checkbox
                                isChecked={present}
                                onChange={() => {
                                  const next = new Set(values);
                                  if (present) {
                                    next.delete(option);
                                  } else {
                                    next.add(option);
                                  }
                                  setParam(key, Array.from(next) as string[]);
                                }}
                              />
                              <span>{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {data.component === 'RadioButton' && data.data && (
                      <RadioGroup
                        name={data.slug}
                        options={data.data}
                        value={getParam(`filters[${data.slug}][]`)}
                        onChange={(value) =>
                          setParam(`filters[${data.slug}][]`, value)
                        }
                        className={styles.radioFilter}
                      />
                    )}

                    {data.component === 'RadioButtonPills' && data.data && (
                      <RadioGroup
                        name={data.slug}
                        options={data.data}
                        value={getParam(`filters[${data.slug}][]`)}
                        onChange={(value) =>
                          setParam(`filters[${data.slug}][]`, value)
                        }
                        className={styles.radioPills}
                      />
                    )}

                    {data.property_type === 'string' && (
                      <ControlledInput
                        type="text"
                        className={''}
                        placeholder={data.title}
                        value={getParam(`filters[${data.slug}]`) || ''}
                        onChange={(value) =>
                          setParam(`filters[${data.slug}]`, value)
                        }
                      />
                    )}

                    {data.property_type === 'boolean' &&
                      data.component !== 'RadioButton' &&
                      data.component !== 'RadioButtonPills' && (
                        <ToggleSwitcher
                          name={`filters[${data.slug}]`}
                          value={getParam(`filters[${data.slug}]`) || '0'}
                          onChange={(value) =>
                            setParam(
                              `filters[${data.slug}]`,
                              value === '1' ||
                                value === 'true' ||
                                value === true
                                ? '1'
                                : null,
                            )
                          }
                        />
                      )}

                    {data.property_type !== 'list' &&
                      data.property_type !== 'string' &&
                      data.property_type !== 'boolean' && (
                        <InputRange
                          filterName={data.title}
                          onChangeFrom={(k, v) => setParam(k, v)}
                          onChangeTo={(k, v) => setParam(k, v)}
                          fromPlaceholder="От"
                          toPlaceholder="До"
                          fromKey={`filters[${data.slug}][from]`}
                          toKey={`filters[${data.slug}][to]`}
                          initialValues={[
                            searchParams?.get(`filters[${data.slug}][from]`) ||
                              '',
                            searchParams?.get(`filters[${data.slug}][to]`) ||
                              '',
                          ]}
                          size={
                            isMaps
                              ? isMobile && window
                                ? window.innerWidth / 2 - 20
                                : 180
                              : isMobile && window
                                ? window.innerWidth / 2 - 20
                                : 145
                          }
                        />
                      )}
                  </div>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div
        className={`${isMaps ? styles.stickyBarMaps : styles.stickyBar} ${isFixed && !isMaps && !isMobile ? styles.fixed : ''}  ${isMobile ? styles.mobileBtn : ''}`}
      >
        <button
          type="button"
          disabled={!hasChanges}
          onClick={() => {
            setLoad?.(true);
            applyPending();
            if (setIsOpenFilter) setIsOpenFilter(false);
          }}
          className={`${styles.showBtn} ${hasChanges ? styles.showBtnActive : ''}`}
        >
          Показать объявления
        </button>
      </div>
    </section>
  );
};

export default CatalogFilters;
